import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  DatabaseIcon,
  PlusIcon,
  TrashIcon,
  UploadSimpleIcon,
  FileTextIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";

export default function Knowledge() {
  const [collections, setCollections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [newName, setNewName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [hits, setHits] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (activeId) loadDocs(activeId);
    else setDocs([]);
  }, [activeId]);

  const refresh = async () => {
    const { data } = await api.get("/collections");
    setCollections(data);
    if (data.length && !activeId) setActiveId(data[0].id);
  };

  const loadDocs = async (cid) => {
    const { data } = await api.get(`/collections/${cid}/documents`);
    setDocs(data);
  };

  const createCollection = async () => {
    if (!newName.trim()) return;
    const { data } = await api.post("/collections", { name: newName });
    setNewName("");
    setCollections((c) => [data, ...c]);
    setActiveId(data.id);
    toast.success(`Collection "${data.name}" created`);
  };

  const deleteCollection = async (cid) => {
    if (!confirm("Delete collection and all its documents?")) return;
    await api.delete(`/collections/${cid}`);
    setCollections((c) => c.filter((x) => x.id !== cid));
    if (activeId === cid) setActiveId(null);
  };

  const upload = async (file) => {
    if (!activeId || !file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/collections/${activeId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Indexed ${file.name}`);
      await Promise.all([refresh(), loadDocs(activeId)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteDoc = async (docId) => {
    if (!confirm("Delete document?")) return;
    await api.delete(`/collections/${activeId}/documents/${docId}`);
    await Promise.all([refresh(), loadDocs(activeId)]);
  };

  const runSearch = async () => {
    if (!searchQ.trim() || !activeId) return;
    const { data } = await api.post("/collections/search", {
      query: searchQ,
      collection_ids: [activeId],
      k: 5,
    });
    setHits(data.hits);
  };

  const active = collections.find((c) => c.id === activeId);

  return (
    <div className="h-full overflow-y-auto" data-testid="knowledge-page">
      <div className="max-w-6xl mx-auto p-8 fade-up">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="font-overline text-[10px] text-zinc-500">
              Vector Memory · ChromaDB
            </div>
            <h1 className="font-heading text-4xl tracking-tighter font-medium">
              Knowledge Base
            </h1>
          </div>
        </div>

        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          <div>
            <div className="font-overline text-[10px] text-zinc-500 mb-2">
              Collections
            </div>
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="new-collection-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New collection..."
                className="rounded-sm bg-black border-zinc-800 text-xs"
              />
              <Button
                data-testid="create-collection-button"
                onClick={createCollection}
                className="rounded-sm bg-white text-black hover:bg-zinc-200"
              >
                <PlusIcon size={14} />
              </Button>
            </div>
            <div className="space-y-1">
              {collections.length === 0 && (
                <div className="text-xs text-zinc-600 font-mono p-3 border border-dashed border-zinc-800 rounded-sm">
                  No collections yet. Create one to start uploading documents.
                </div>
              )}
              {collections.map((c) => (
                <div
                  key={c.id}
                  data-testid={`collection-row-${c.id}`}
                  className={`group border rounded-sm px-3 py-2.5 cursor-pointer flex items-center justify-between ${
                    activeId === c.id
                      ? "border-zinc-600 bg-zinc-900"
                      : "border-zinc-800 hover:border-zinc-700 bg-zinc-950"
                  }`}
                  onClick={() => setActiveId(c.id)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <DatabaseIcon size={14} className="text-zinc-500" />
                    <div className="min-w-0">
                      <div className="text-xs truncate">{c.name}</div>
                      <div className="text-[10px] font-mono text-zinc-600">
                        {c.doc_count} docs · {c.chunk_count} chunks
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteCollection(c.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            {!active && (
              <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-600 font-mono text-xs">
                Select or create a collection.
              </div>
            )}
            {active && (
              <>
                <div className="mb-6">
                  <div className="font-overline text-[10px] text-zinc-500">
                    Collection
                  </div>
                  <div className="font-heading text-2xl tracking-tight">
                    {active.name}
                  </div>
                </div>

                {/* Upload */}
                <label
                  data-testid="upload-zone"
                  className="block border border-dashed border-zinc-800 hover:border-zinc-600 bg-zinc-950 rounded-sm p-8 text-center cursor-pointer transition-colors"
                >
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept=".txt,.md,.pdf,.json,.csv,.html,.py,.js,.ts"
                    onChange={(e) => upload(e.target.files?.[0])}
                  />
                  <UploadSimpleIcon
                    size={28}
                    className="mx-auto text-zinc-500 mb-2"
                  />
                  <div className="text-sm">
                    {uploading
                      ? "Indexing..."
                      : "Drop or click to add a document"}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-600 mt-1">
                    .txt .md .pdf .json .csv .html .py .js .ts
                  </div>
                </label>

                {/* Doc list */}
                <div className="mt-6">
                  <div className="font-overline text-[10px] text-zinc-500 mb-2">
                    Documents ({docs.length})
                  </div>
                  <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-900">
                    {docs.length === 0 && (
                      <div className="px-4 py-6 text-xs text-zinc-600 font-mono text-center">
                        no documents
                      </div>
                    )}
                    {docs.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-900 group"
                      >
                        <FileTextIcon
                          size={16}
                          className="text-zinc-500 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs truncate">{d.filename}</div>
                          <div className="text-[10px] font-mono text-zinc-600">
                            {(d.size / 1024).toFixed(1)} KB · {d.chunks} chunks
                          </div>
                        </div>
                        <button
                          onClick={() => deleteDoc(d.id)}
                          className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"
                        >
                          <TrashIcon size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Test search */}
                <div className="mt-8">
                  <div className="font-overline text-[10px] text-zinc-500 mb-2">
                    Test Semantic Search
                  </div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="rag-search-input"
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      placeholder="Query this collection..."
                      onKeyDown={(e) => e.key === "Enter" && runSearch()}
                      className="rounded-sm bg-black border-zinc-800 font-mono text-xs"
                    />
                    <Button
                      data-testid="rag-search-button"
                      onClick={runSearch}
                      className="rounded-sm bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"
                    >
                      <MagnifyingGlassIcon size={14} />
                    </Button>
                  </div>
                  {hits && (
                    <div className="mt-3 space-y-2">
                      {hits.length === 0 && (
                        <div className="text-xs text-zinc-600 font-mono">
                          No matches.
                        </div>
                      )}
                      {hits.map((h, i) => (
                        <div
                          key={i}
                          className="border border-zinc-800 bg-black rounded-sm p-3"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Badge
                              variant="outline"
                              className="rounded-sm border-zinc-700 text-zinc-300 font-mono text-[10px]"
                            >
                              {h.metadata?.filename || "?"}
                            </Badge>
                            <span className="font-mono text-[10px] text-zinc-600">
                              dist {h.distance?.toFixed(3)}
                            </span>
                          </div>
                          <div className="text-xs text-zinc-300 whitespace-pre-wrap line-clamp-4">
                            {h.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
